import { XIcon } from "@heroicons/react/outline";
import { MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import useSWR from "swr";
import { getData, OAuthError, PaidFeatureError, postData, putData } from "../backend";
import { useModal } from "../contexts";
import useWorkflow from "../hooks/useWorkflow";
import { Region, SignatureConfig, SignatureField } from "../interfaces";
import { BLOCK_ICON_MAP } from "../utils";
import ErrorModal from "./ErrorModal";
import OAuthModal from "./OAuthModal";
import PayWallModalPro from "./PayWallModalPro";
import PDFViewer from "./PDFViewer";
import SignaturesDraggableField from "./SignaturesDraggableField";
import SignaturesSidebarCompleted from "./SignaturesSidebarCompleted";
import SignaturesSidebarConfirmation from "./SignaturesSidebarConfirmation";
import SignaturesSidebarOverview from "./SignaturesSidebarOverview";
import SignaturesSidebarRecipients from "./SignaturesSidebarRecipients";
import SignaturesSidebarRegions from "./SignaturesSidebarRegions";

type Stage = "recipeints" | "regions" | "confirmation" | "completed";

const SIZE_MAP: Record<SignatureField, { width: number, height: number }> = {
  "SIGNATURE": { width: 280, height: 80 },
  "INITIALS": { width: 140, height: 40 },
  "DATE": { width: 140, height: 40 },
  "TEXT": { width: 280, height: 40 },
  "TEXTAREA": { width: 280, height: 200 },
  "CHECKBOX": { width: 24, height: 24 },
};

function SignaturesDetail() {
  const { openModal } = useModal();

  const [stage, setStage] = useState<Stage>("recipeints");
  const [isSending, setIsSending] = useState(false);

  const history = useHistory();
  const { t } = useTranslation();

  const { id } = useParams<{ id: string | undefined }>();

  const { workflow, isLoading } = useWorkflow(id!);

  const { data: documentUrlData } = useSWR(`/signatures/document-url/${id}/`, getData);
  const { url: completedPdfUrl } = documentUrlData ?? {};

  const document = workflow?.documents[0];
  const [pdfUrl, setPdfUrl] = useState<string>();

  const [currentPage, setCurrentPage] = useState(1);
  const [currentScale, setCurrentScale] = useState(1);

  const [fieldRegions, setFieldRegions] = useState<Array<Region>>();

  const [signatureConfig, setSignatureConfig] = useState<SignatureConfig>();

  const [currentSelectedField, setCurrentSelectedField] = useState<{ field: SignatureField, recipient: number }>();
  const [currentPosition, setCurrentPosition] = useState({ left: 0, top: 0 });
  const [currentOffset, setCurrentOffset] = useState({ left: 0, top: 0 });

  const pageRects = useRef<Record<number, DOMRect>>({});

  const fetchPdfUrl = useCallback(() => {
    if (!document)
      return;

    postData(`/workflows/documents/${document!.id}/generate-pdf-download-url/`)
      .then(({ url }) => setPdfUrl(url))
      .catch(() => console.error);
  }, [document]);

  useEffect(() => {
    fetchPdfUrl();
  }, [fetchPdfUrl]);

  useEffect(() => {
    if (!workflow || !document || signatureConfig || fieldRegions || !workflow.signatureConfig)
      return;

    setSignatureConfig(workflow.signatureConfig);
    setFieldRegions(document.fieldRegions);

  }, [workflow, document, signatureConfig, fieldRegions])

  function updateSignatureConfig(updates: Partial<SignatureConfig>) {
    setSignatureConfig((prev) => ({ ...prev!, ...updates }));
  }

  async function handleSaveAndNext() {
    if (stage === "recipeints") {
      // NO await
      putData(`/signatures/signature-configs/${signatureConfig!.id}/`, signatureConfig)
        .catch(console.error);
    }

    else if (stage === "regions") {
      // NO await
      putData(`/workflows/documents/${document!.id}/`, { ...document, fieldRegions })
        .catch(console.error);
    }

    else if (stage === "confirmation") {
      setIsSending(true);

      try {
        await putData(`/signatures/signature-configs/${signatureConfig!.id}/`, signatureConfig)
        await postData(`/workflows/${workflow!.id}/run/`, { options: {} })

      } catch (error) {
        if (error instanceof OAuthError)
          openModal(<OAuthModal oauthUrl={error.oauthUrl} />)

        else if (error instanceof PaidFeatureError)
          openModal(<PayWallModalPro reason={error.reason} exceededAmount={error.payload?.["amount"]} />)

        else
          openModal(<ErrorModal details={error} />)

        return; // do not progress stage

      } finally {
        setIsSending(false);
      }
    }

    const stages: Array<Stage> = ["recipeints", "regions", "confirmation", "completed"];
    setStage(stages[stages.indexOf(stage) + 1]);
  }

  function handleBack() {
    if (stage === "recipeints" || stage === "completed")
      return history.push("/signatures")

    const stages: Array<Stage> = ["recipeints", "regions", "confirmation", "completed"];
    setStage(stages[stages.indexOf(stage) - 1]);
  }

  function handleMouseMove(e: MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    setCurrentPosition({ left: e.clientX - rect.left, top: e.clientY - rect.top });
  }

  function handleMouseUp(e: MouseEvent<HTMLDivElement>) {
    if (!currentSelectedField)
      return;

    const id = String(new Date().getTime());

    const pageRect = pageRects.current[currentPage];

    const left = e.clientX - pageRect.left;
    const top = e.clientY - pageRect.top;

    const size = SIZE_MAP[currentSelectedField.field];

    if ((0 > left || left > pageRect.width) || 0 > top || top > pageRect.height) {
      setCurrentSelectedField(undefined);
      return;
    }

    setFieldRegions((prev) => [...(prev || []), {
      id,
      type: currentSelectedField.field,
      recipient: currentSelectedField!.recipient,
      rect: {
        x: left - currentOffset.left,
        y: top - currentOffset.top,
        ...size,
        scale: currentScale,
      },
      pageNumber: currentPage,
    }]);

    setCurrentSelectedField(undefined);
  }

  const updatePageRects = useCallback((n: number, rect: DOMRect) => {
    pageRects.current = { ...pageRects.current, [n]: rect };
  }, []);

  return (
    <div className='relative flex bg-gray-100' style={{ height: `calc(100vh)` }} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
      {currentSelectedField &&
        <SignaturesDraggableField field={currentSelectedField.field} recipient={currentSelectedField.recipient} position={currentPosition} offset={currentOffset} />
      }

      <div className="border border-gray-200 rounded-lg bg-white p-4 m-4 w-full">
        <div className="flex w-full items-center pb-1 border-b h-[52px]">
          <div className="flex w-full items-center mb-2">
            <div className="flex justify-center items-center my-auto mr-2 rounded w-10 h-10">
              <img className="h-10 w-10 rounded-md" src={BLOCK_ICON_MAP[document?.documentType ?? "PDF_FILLABLE"]} alt="PDF File Icon" />
            </div>
            <div className="flex flex-col h-full justify-center">
              <div className="flex text-sm font-semibold font-gilroy cursor-pointer hover:text-blue hover:underline" onClick={() => window.open(document?.file?.url, "_blank")}>
                <div className="truncate max-w-[400px]">
                  {document?.config["filename"] ?? "PDF Document"}
                </div>
              </div>
            </div>
            <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push("/signatures/")} />
          </div>
        </div>

        <div className="flex flex-col" style={{ height: `calc(100vh - 52px - 64px)` }}>
          {completedPdfUrl &&
            <PDFViewer pdfUrl={completedPdfUrl} />
          }
          {(pdfUrl && !completedPdfUrl) &&
            <PDFViewer pdfUrl={pdfUrl} setCurrentPage={setCurrentPage} setCurrentScale={setCurrentScale} regions={fieldRegions} updateRegions={setFieldRegions} updatePageRects={updatePageRects} disabled={!!workflow?.latestAutomation || stage !== "regions"} />
          }
          {(!pdfUrl && !completedPdfUrl && !document && !isLoading) &&
            <div className="w-full h-[800px] flex items-center justify-center text-red font-semibold">{t("An issue occured, please archive this workflow and start over.")}</div>
          }
        </div>
      </div>

      {!!workflow?.latestAutomation &&
        <SignaturesSidebarOverview workflow={workflow} handleBack={handleBack} handleRefresh={() => fetchPdfUrl()} />
      }

      {(signatureConfig && !workflow?.latestAutomation) &&
        <>
          {stage === "recipeints" &&
            <SignaturesSidebarRecipients signatureConfig={signatureConfig} updateSignatureConfig={updateSignatureConfig} handleSaveAndNext={handleSaveAndNext} handleBack={handleBack} />
          }
          {stage === "regions" &&
            <SignaturesSidebarRegions signatureConfig={signatureConfig} updateSignatureConfig={updateSignatureConfig} handleSaveAndNext={handleSaveAndNext} handleBack={handleBack} setCurrentField={(f, r, offset) => { setCurrentSelectedField({ field: f, recipient: r }); setCurrentOffset(offset) }} currentField={currentSelectedField} />
          }
          {stage === "confirmation" &&
            <SignaturesSidebarConfirmation signatureConfig={signatureConfig} updateSignatureConfig={updateSignatureConfig} handleSaveAndNext={handleSaveAndNext} handleBack={handleBack} isSending={isSending} />
          }
          {stage === "completed" &&
            <SignaturesSidebarCompleted handleBack={handleBack} />
          }
        </>
      }
    </div>
  );

}

export default SignaturesDetail;
